/**
 * ── LocalStorage Cache Utility ──────────────────────────────────────────────
 *
 * Provides a TTL-based caching layer using localStorage.
 * Two cache namespaces:
 *   - solar:    NASA POWER climate data    → 24h TTL (historical, rarely changes)
 *   - forecast: Open-Meteo live forecast   → 1h TTL  (changes hourly)
 *
 * Key format: "ags_cache:{namespace}:{lat4},{lng4}"
 */

const CACHE_PREFIX = 'ags_cache_v4';

// TTLs in milliseconds
const TTL = {
    solar:    24 * 60 * 60 * 1000,  // 24 hours
    forecast:  1 * 60 * 60 * 1000,  // 1 hour
};

// Maximum entries per namespace before pruning old ones
const MAX_ENTRIES = 500;

/**
 * Build a localStorage key from namespace + coordinates
 */
const cacheKey = (namespace, lat, lng) =>
    `${CACHE_PREFIX}:${namespace}:${parseFloat(lat).toFixed(4)},${parseFloat(lng).toFixed(4)}`;

/**
 * Get a cached value. Returns null if missing or expired.
 */
export function getCached(namespace, lat, lng) {
    try {
        const key = cacheKey(namespace, lat, lng);
        const raw = localStorage.getItem(key);
        if (!raw) return null;

        const { data, timestamp } = JSON.parse(raw);
        const ttl = TTL[namespace] || TTL.forecast;

        if (Date.now() - timestamp > ttl) {
            // Expired — remove and return null
            localStorage.removeItem(key);
            return null;
        }

        return data;
    } catch (err) {
        console.warn('Cache read error:', err);
        return null;
    }
}

/**
 * Store a value in the cache with current timestamp.
 */
export function setCache(namespace, lat, lng, data) {
    try {
        const key = cacheKey(namespace, lat, lng);
        const entry = JSON.stringify({
            data,
            timestamp: Date.now(),
        });
        localStorage.setItem(key, entry);
    } catch (err) {
        // localStorage full — prune old entries and retry
        if (err.name === 'QuotaExceededError' || err.code === 22) {
            pruneCache(namespace);
            try {
                const key = cacheKey(namespace, lat, lng);
                localStorage.setItem(key, JSON.stringify({
                    data,
                    timestamp: Date.now(),
                }));
            } catch {
                // Give up silently
            }
        }
    }
}

/**
 * Get multiple cached values at once.
 * Returns a Map of "lat4,lng4" → data for all cache hits.
 */
export function getCachedBatch(namespace, coordsList) {
    const results = new Map();
    for (const { lat, lng } of coordsList) {
        const cached = getCached(namespace, lat, lng);
        if (cached) {
            results.set(
                `${parseFloat(lat).toFixed(4)},${parseFloat(lng).toFixed(4)}`,
                cached
            );
        }
    }
    return results;
}

/**
 * Cache multiple results at once.
 * Each result must have { lat, lng, ...data }.
 */
export function setCacheBatch(namespace, results) {
    for (const result of results) {
        if (result.lat != null && result.lng != null) {
            setCache(namespace, result.lat, result.lng, result);
        }
    }
}

/**
 * Remove all expired entries for a namespace, plus oldest entries
 * if we exceed MAX_ENTRIES.
 */
export function pruneCache(namespace) {
    try {
        const prefix = `${CACHE_PREFIX}:${namespace}:`;
        const entries = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(prefix)) {
                try {
                    const { timestamp } = JSON.parse(localStorage.getItem(key));
                    const ttl = TTL[namespace] || TTL.forecast;

                    if (Date.now() - timestamp > ttl) {
                        localStorage.removeItem(key);
                    } else {
                        entries.push({ key, timestamp });
                    }
                } catch {
                    localStorage.removeItem(key);
                }
            }
        }

        // If still over limit, remove oldest
        if (entries.length > MAX_ENTRIES) {
            entries.sort((a, b) => a.timestamp - b.timestamp);
            const toRemove = entries.slice(0, entries.length - MAX_ENTRIES);
            for (const { key } of toRemove) {
                localStorage.removeItem(key);
            }
        }
    } catch (err) {
        console.warn('Cache prune error:', err);
    }
}

/**
 * Get cache stats (for debugging / dev tools).
 */
export function getCacheStats() {
    const stats = { solar: 0, forecast: 0, totalBytes: 0 };
    try {
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(`${CACHE_PREFIX}:solar:`)) {
                stats.solar++;
                stats.totalBytes += (localStorage.getItem(key) || '').length;
            } else if (key?.startsWith(`${CACHE_PREFIX}:forecast:`)) {
                stats.forecast++;
                stats.totalBytes += (localStorage.getItem(key) || '').length;
            }
        }
    } catch {}
    return stats;
}
